import {assetsSnapshot,assetsConfirmText} from './workflowAssetTasks';
import {newRequestId} from './workflowPrimaryPolicy';
// 261010 Epic 2 / P3c — KHỐI hồ sơ phong cách cấp truyện đặt trên đầu bước 2 Tài sản (trên ba tab của P4b-2): dải + panel. Chủ dữ liệu `GET style-profile` (một nguồn cho cả dải và panel).
// Gọi API, kiểm trước (FE kiểm cùng luật BE — BE là chuẩn), poll khi job đề xuất chạy, lỗi bằng chữ. Chữ / luật quyết ở styleProfilePolicy.ts (có test); poll ở styleProfilePoller.ts.
// Job đề xuất chạy NỀN (202) nên lỗi của nó (đếm token, AI, CAS) KHÔNG đi theo response: khi job kết thúc đọc `failures[id = jobId]` của `GET /plans/:id/jobs`.
// Sau MỌI thao tác ghi gọi `onActionDone` để trang tải lại `workflow-status` (alert STYLE_PROFILE_NOT_APPROVED đổi theo trạng thái hồ sơ).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { filmApi } from '../../services/api/film';
import { takeWorkflowHandoff } from '../../services/api/workflowReadHandoff';
import { AK } from './workflowAssetsPreload';
import { useAliveRef } from '../../hooks/useAliveRef';
import type { FilmRunningJob } from '../../types/film';
import type { StyleIssue, StyleProfileView } from '../../types/filmStyleProfile';
import { apiErrorText } from './apiErrorText';
import { createStyleProfilePoller, type StyleProfilePoller } from './styleProfilePoller';
import {
  REPLACE_CONFIRM_BODY, REPLACE_CONFIRM_TITLE, approveIssues, baseRevisionOf, contentOf, draftFailureNote, draftIssues, isRunActive, issueLine, issuesByBlock, putBody, stripOf,
  styleActions, styleProfileActionError, writeGate, type BlockValue, type StyleActionError, type StyleBlockKey,
} from './styleProfilePolicy';
import WorkflowStyleStrip from './WorkflowStyleStrip';
import WorkflowStyleProfilePanel from './WorkflowStyleProfilePanel';
import WorkflowStyleNotice from './WorkflowStyleNotice';
import './styleProfile.css';
import { needsConfirm } from './workflowConfirmPolicy';

interface Props {
  planId: string;
  /** BE `GET style-profile` KHÔNG trả tên truyện ⇒ trang chưa có thì dải nói "của truyện này". */
  storyName?: string | null;
  /** Việc nền của plan (từ `workflow-status`) — để khoá nút khi máy tự chạy đang chạy. */
  jobs?: FilmRunningJob[];
  onActionDone: () => void;
}

type Lines = Record<StyleBlockKey | 'GENERAL', string[]>;
const NO_LINES: Lines = { audience: [], rules: [], rhythm: [], look: [], GENERAL: [] };

const WorkflowStyleSection: React.FC<Props> = ({ planId, storyName, jobs, onActionDone }) => {
  const alive = useAliveRef();
  const [view, setView] = useState<StyleProfileView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pollError, setPollError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState<Partial<Record<StyleBlockKey, boolean>>>({});
  const [actionError, setActionError] = useState<StyleActionError | null>(null);
  const [lines, setLines] = useState<Lines>(NO_LINES);
  const [draftFailure, setDraftFailure] = useState<string | null>(null);
  const poller = useRef<StyleProfilePoller | null>(null);
  const planRef = useRef(planId);
  planRef.current = planId;
  const doneRef = useRef(onActionDone);
  doneRef.current = onActionDone;

  const apply = useCallback((v: StyleProfileView) => {
    if (!alive.current) return;
    setView(v); setLoadError(null);
    poller.current?.sync(v);
    setPollError(!!poller.current?.isErrored()); // poll chết vì lỗi mạng thì dòng báo phải còn tới khi bấm Tải lại — sau thao tác ghi cũng không xoá
  }, [alive]);

  const load = useCallback(async (first = false) => {
    const id = planId;
    // P3 F1: chỉ lần nạp ĐẦU (effect mount) lấy từ chỗ giao; «Tải lại» / sau ghi gọi `load()` ⇒ gọi mới.
    try { const v = await (first === true ? takeWorkflowHandoff(AK.style(id), () => filmApi.getStyleProfile(id)) : filmApi.getStyleProfile(id)); if (planRef.current === id) apply(v); }
    catch (e) { if (alive.current && planRef.current === id) setLoadError(apiErrorText(e, 'Tải hồ sơ phong cách')); }
  }, [planId, apply, alive]);

  // Job đề xuất vừa kết thúc ⇒ trang tải lại workflow-status; lỗi của job nằm ở `failures` (note = câu tiếng Việt đã làm sạch).
  const onFinished = useCallback(async (jobId: string) => {
    doneRef.current();
    try {
      const r = await filmApi.getPlanJobs(planRef.current);
      if (alive.current) setDraftFailure(draftFailureNote(r.failures, jobId));
    } catch { /* không đọc được failures: hồ sơ vẫn là sự thật; người dùng thấy dải */ }
  }, [alive]);

  useEffect(() => {
    const p = createStyleProfilePoller({
      fetchView: () => filmApi.getStyleProfile(planId),
      onData: (v) => { setView(v); setPollError(false); },
      onFinished: (jobId) => { void onFinished(jobId); },
      onError: () => setPollError(true),
    });
    poller.current = p;
    setView(null); setLoadError(null); setPollError(false); setBusy(null); setActionError(null); setLines(NO_LINES); setDraftFailure(null); setDirty({}); setOpen(false);
    void load(true);
    return () => { p.stop(); poller.current = null; };
  }, [planId, load, onFinished]);

  const runActive = isRunActive(jobs);
  const gate = writeGate(view, { busy: busy !== null, runActive });
  const dirtyKeys = useMemo(() => (Object.keys(dirty) as StyleBlockKey[]).filter((k) => dirty[k]), [dirty]);
  const actions = useMemo(() => styleActions(view, { busy: busy !== null, runActive, dirty: dirtyKeys }), [view, busy, runActive, dirtyKeys]);
  const onDirty = useCallback((block: StyleBlockKey, d: boolean) => setDirty((p) => (!!p[block] === d ? p : { ...p, [block]: d })), []);

  const showIssues = (issues: StyleIssue[], sent: Array<{ id: string }>) => {
    const g = issuesByBlock(issues);
    const out = { ...NO_LINES };
    (Object.keys(g) as Array<StyleBlockKey | 'GENERAL'>).forEach((k) => { out[k] = g[k].map((i) => issueLine(i, sent)); });
    setLines(out);
  };
  const reset = () => { setActionError(null); setLines(NO_LINES); };

  /** Lỗi API ⇒ chữ: lỗi kiểm tra nhập liệu hiện dưới khối; lỗi khác hiện ở đầu khối + nạp lại hồ sơ (CAS lệch / job đang chạy…). */
  const fail = async (e: unknown, what: string, sent: Array<{ id: string }>) => {
    const err = styleProfileActionError(e, what);
    if (err.kind === 'INVALID' && err.issues.length) showIssues(err.issues, sent);
    setActionError(err);
    if (err.reload) await load();
  };

  const doGenerate = async () => {
    setBusy('generate'); reset(); setDraftFailure(null);
    try {
      const preview=await filmApi.getAssetsPreview(planId);
      if(preview.enabled){
        const ok=needsConfirm('ASSETS','RUN')?await new Promise<boolean>(resolve=>Modal.confirm({title:'Đề xuất hồ sơ bằng AI',content:'Tác vụ chữ trả phí qua runner tài sản; tối đa trần lượt AI hiện hành. Hồ sơ đã có sẽ được thay bằng bản nháp mới.',onOk:()=>resolve(true),onCancel:()=>resolve(false)})):true; // U9: chạy ngay (chỗ «thay hồ sơ đã có» vẫn hỏi ở onGenerate)
        if(ok){const r=await filmApi.runWorkflowStep(planId,'ASSETS',{scope:'missing',requestId:newRequestId(),confirm:true,assetsPreview:assetsSnapshot(preview),targetKeys:['prepare:style-draft'],styleBaseRevision:view?.profile?.revision||0});if(r.jobId)poller.current?.track(r.jobId);}
      }else{const r = await filmApi.draftStyleProfile(planId); poller.current?.track(r.jobId);}
    }
    catch (e) { await fail(e, 'AI đề xuất', contentOf(view?.profile ?? null).rules); }
    try { const v = await filmApi.getStyleProfile(planId); if (planRef.current === planId) apply(v); } catch { /* poll / Tải lại sẽ nạp */ }
    if (alive.current) setBusy(null);
    doneRef.current();
  };
  const onGenerate = () => {
    if (actions.find((a) => a.key === 'STYLE_GENERATE')?.confirm === 'REPLACE') {
      Modal.confirm({ title: REPLACE_CONFIRM_TITLE, content: REPLACE_CONFIRM_BODY, okText: 'Thay bằng bản AI', cancelText: 'Giữ bản hiện tại', onOk: () => { void doGenerate(); } });
    } else void doGenerate();
  };

  const onSave = async (block: StyleBlockKey, value: BlockValue): Promise<boolean> => {
    if (!view) return false;
    const body = putBody(view.profile, block, value);
    reset();
    const issues = draftIssues(body);
    if (issues.length) { showIssues(issues, body.rules); return false; } // FE kiểm trước; BE vẫn là chuẩn
    setBusy(`save:${block}`);
    let ok = false;
    try { apply(await filmApi.saveStyleProfile(planId, body)); ok = true; }
    catch (e) { await fail(e, 'Lưu hồ sơ phong cách', body.rules); }
    if (alive.current) setBusy(null);
    doneRef.current();
    return ok;
  };

  const onApprove = async () => {
    if (!view?.profile) return;
    reset();
    const issues = approveIssues(contentOf(view.profile));
    if (issues.length) { showIssues(issues, view.profile.rules); return; }
    setBusy('approve');
    try { apply(await filmApi.approveStyleProfile(planId, baseRevisionOf(view))); }
    catch (e) { await fail(e, 'Duyệt hồ sơ phong cách', view.profile.rules); }
    if (alive.current) setBusy(null);
    doneRef.current();
  };

  const strip = stripOf(view, storyName);
  return (
    <section className="wf-sty" aria-label="Hồ sơ phong cách của truyện" data-testid="wf-sty">
      <WorkflowStyleStrip strip={strip} open={open} generate={actions.find((a) => a.key === 'STYLE_GENERATE')} onToggle={() => setOpen((o) => !o)} onGenerate={onGenerate} />
      {loadError ? <div className="wf-alert ERROR" role="alert">{loadError} <button type="button" className="wf-btn" onClick={() => { void load(); }}>Tải lại</button></div> : null}
      {pollError ? <div className="wf-alert WARN" role="status">Không cập nhật được trạng thái đề xuất. <button type="button" className="wf-btn" onClick={() => { void poller.current?.reload(); }}>Tải lại</button></div> : null}
      {draftFailure ? <div className="wf-alert ERROR" role="alert">{draftFailure} <button type="button" className="wf-btn ghost" onClick={() => setDraftFailure(null)}>Đóng</button></div> : null}
      {actionError ? (
        <div className="wf-alert ERROR" role="alert">{actionError.text}
          {actionError.reload ? <> <button type="button" className="wf-btn" onClick={() => { void load(); }}>Tải lại</button></> : null}
          <button type="button" className="wf-btn ghost" onClick={reset}>Đóng</button>
        </div>) : null}
      {/* UI-assets: «Máy đã làm sạch chữ phong cách» + Hoàn tác nằm TRONG thẻ này (chỉ thấy khi mở), không còn ở thân bước. */}
      {open ? <WorkflowStyleNotice planId={planId} onDone={() => doneRef.current()} /> : null}
      {open && view ? (
        <WorkflowStyleProfilePanel view={view} actions={actions} gate={gate} errors={lines} onGenerate={onGenerate} onApprove={() => { void onApprove(); }} onSave={onSave} onDirty={onDirty} />
      ) : null}
    </section>
  );
};

export default WorkflowStyleSection;
